import {
    Arrival,
    CompoundLocation,
    DepartedVehicle,
    DepartedVehiclesPage,
    Departure,
    ExpectedArrival,
    ExpectedDeparture,
    FreightForwarder,
    IncomingVehicle,
    IncomingVehicleInLoadPreview,
    IncomingVehiclesPage,
    InventoryVehicle,
    InventoryVehiclesPage,
    LoadPreviewArrivals,
    LoadPreviewDepartures,
    LoadPreviewError,
    LoadPreviewErrorCode,
    LoadPreviewUnannouncedDepartures,
    LoadValidationRequest,
    OutgoingVehicle,
    OutgoingVehicleInLoadPreview,
    OutgoingVehiclesPage,
    TransportInformation,
    VehicleInLoad,
    VehicleInUnannouncedLoadPreview,
} from '../../../compoundManagement/compoundManagement.types';
import { parseStringDate } from '../../../sharedComponents/dateHelper';
import {
    ApiArrival,
    ApiCompoundLocation,
    ApiDepartedVehicle,
    ApiDepartedVehiclesPage,
    ApiDeparture,
    ApiExpectedArrival,
    ApiExpectedDeparture,
    ApiFreightForwarder,
    ApiIncomingVehicle,
    ApiIncomingVehiclesPage,
    ApiInventoryVehicle,
    ApiInventoryVehiclesPage,
    ApiLoadPreview,
    ApiLoadPreviewError,
    ApiLoadPreviewErrorCode,
    ApiLoadValidationRequest,
    ApiOutgoingVehicle,
    ApiOutgoingVehiclesPage,
    ApiTransportInformation,
    ApiUnannouncedLoadPreviewDepartures,
    ApiVehicleBulkSelection,
    ApiVehicleInLoadPreview,
    ApiVehicleInUnannouncedLoadPreview,
} from '../../types/compoundManagement/compoundManagementApi.types';
import { mapToAddress } from '../addressMappers';
import { mapToVehicleType } from '../sharedVehicleMappers';
import { mapToVehicle } from './vehicleCompoundMapper';
import { mapToApiMeansOfTransportType, mapToMeansOfTransportType } from '../meansOfTransportTypeMapper';
import { mapToLocation } from './locationMapper';
import { NonEmptyString } from 'io-ts-types';

export const mapToIncomingVehicle = (apiVehicle: ApiIncomingVehicle): IncomingVehicle => ({
    id: apiVehicle.id,
    compoundLocation: mapToCompoundLocation(apiVehicle.compound_location),
    arrival: mapToExpectedArrival(apiVehicle.arrival),
    vehicle: mapToVehicle(apiVehicle.vehicle),
});

export const mapToInventoryVehicle = (apiVehicle: ApiInventoryVehicle): InventoryVehicle => ({
    id: apiVehicle.id,
    compoundLocation: mapToCompoundLocation(apiVehicle.compound_location),
    arrival: mapToArrival(apiVehicle.arrival),
    vehicle: mapToVehicle(apiVehicle.vehicle),
});

export const mapToOutgoingVehicle = (apiVehicle: ApiOutgoingVehicle): OutgoingVehicle => ({
    id: apiVehicle.id,
    compoundLocation: mapToCompoundLocation(apiVehicle.compound_location),
    departure: mapToExpectedDeparture(apiVehicle.departure),
    vehicle: mapToVehicle(apiVehicle.vehicle),
});

export const mapToDepartedVehicle = (apiVehicle: ApiDepartedVehicle): DepartedVehicle => ({
    id: apiVehicle.id,
    compoundLocation: mapToCompoundLocation(apiVehicle.compound_location),
    arrival: apiVehicle.arrival ? mapToArrival(apiVehicle.arrival) : undefined,
    departure: mapToDeparture(apiVehicle.departure),
    vehicle: mapToVehicle(apiVehicle.vehicle),
});

export const mapToIncomingVehiclesPage = (apiPage: ApiIncomingVehiclesPage): IncomingVehiclesPage => ({
    items: apiPage.items.map(mapToIncomingVehicle),
    hasMore: apiPage.has_more,
});

export const mapToCompoundVehicleIds = (apiPage: ApiVehicleBulkSelection): string[] => apiPage.compound_vehicle_ids;

export const mapToInventoryVehiclesPage = (apiPage: ApiInventoryVehiclesPage): InventoryVehiclesPage => ({
    items: apiPage.items.map(mapToInventoryVehicle),
    hasMore: apiPage.has_more,
});

export const mapToOutgoingVehiclesPage = (apiPage: ApiOutgoingVehiclesPage): OutgoingVehiclesPage => ({
    items: apiPage.items.map(mapToOutgoingVehicle),
    hasMore: apiPage.has_more,
});

export const mapToDepartedVehiclesPage = (apiPage: ApiDepartedVehiclesPage): DepartedVehiclesPage => ({
    items: apiPage.items.map(mapToDepartedVehicle),
    hasMore: apiPage.has_more,
});

const mapToCompoundLocation = (apiCompoundLocation: ApiCompoundLocation): CompoundLocation => ({
    identifier: apiCompoundLocation.identifier,
    address: mapToAddress(apiCompoundLocation.address),
    timeZone: apiCompoundLocation.time_zone,
});

export const mapToFreightForwarder = (apiFreightForwarder: ApiFreightForwarder): FreightForwarder => ({
    name: apiFreightForwarder.name,
    additionalName: apiFreightForwarder.additional_name,
});

export const mapToExpectedArrival = (apiExpectedArrival: ApiExpectedArrival): ExpectedArrival => ({
    expectedArrivalAt: parseStringDate(apiExpectedArrival.expected_arrival_at),
    transportInformation: mapToTransportInformation(apiExpectedArrival.transport_information),
    freightForwarder:
        apiExpectedArrival.freight_forwarder && mapToFreightForwarder(apiExpectedArrival.freight_forwarder),
    arrivalFrom: apiExpectedArrival.arrival_from && mapToLocation(apiExpectedArrival.arrival_from),
    isCancelled: apiExpectedArrival.is_cancelled,
    editable: apiExpectedArrival.editable,
});

export const mapToArrival = (apiArrival: ApiArrival): Arrival => ({
    arrivalCompletedAt: parseStringDate(apiArrival.arrival_completed_at),
    transportInformation: mapToTransportInformation(apiArrival.transport_information),
    freightForwarder: apiArrival.freight_forwarder && mapToFreightForwarder(apiArrival.freight_forwarder),
    arrivalFrom: apiArrival.arrival_from && mapToLocation(apiArrival.arrival_from),
    isCancelled: apiArrival.is_cancelled,
    isFinalized: apiArrival.is_finalized,
});

export const mapToExpectedDeparture = (apiExpectedDeparture: ApiExpectedDeparture): ExpectedDeparture => ({
    expectedDepartureAt: parseStringDate(apiExpectedDeparture.expected_departure_at),
    transportInformation: mapToTransportInformation(apiExpectedDeparture.transport_information),
    freightForwarder:
        apiExpectedDeparture.freight_forwarder && mapToFreightForwarder(apiExpectedDeparture.freight_forwarder),
    departureTo: apiExpectedDeparture.departure_to && mapToLocation(apiExpectedDeparture.departure_to),
    isCancelled: apiExpectedDeparture.is_cancelled,
    editable: apiExpectedDeparture.editable,
});

export const mapToDeparture = (apiDeparture: ApiDeparture): Departure => ({
    departureCompletedAt: parseStringDate(apiDeparture.departure_completed_at),
    transportInformation: mapToTransportInformation(apiDeparture.transport_information),
    freightForwarder: apiDeparture.freight_forwarder && mapToFreightForwarder(apiDeparture.freight_forwarder),
    departureTo: apiDeparture.departure_to && mapToLocation(apiDeparture.departure_to),
    isFinalized: apiDeparture.is_finalized,
    isCancelled: apiDeparture.is_cancelled,
});

export const mapToTransportInformation = (apiTransportInformation: ApiTransportInformation): TransportInformation => ({
    meansOfTransportType: mapToMeansOfTransportType(apiTransportInformation.means_of_transport_type),
    meansOfTransportId: apiTransportInformation.means_of_transport_id,
    meansOfTransportName: apiTransportInformation.means_of_transport_name,
    trailerId: apiTransportInformation.trailer_id,
    journeyId: apiTransportInformation.journey_id,
    loadId: apiTransportInformation.load_id,
});

export const mapToApiTransportInformation = (transportInformation: TransportInformation): ApiTransportInformation => ({
    means_of_transport_type: mapToApiMeansOfTransportType(transportInformation.meansOfTransportType),
    means_of_transport_id: transportInformation.meansOfTransportId,
    means_of_transport_name: transportInformation.meansOfTransportName,
    trailer_id: transportInformation.trailerId,
    journey_id: transportInformation.journeyId,
    load_id: transportInformation.loadId,
});

export const mapToApiLoadValidationRequest = (request: LoadValidationRequest): ApiLoadValidationRequest => ({
    compound_vehicle_ids: request.ids as NonEmptyString[],
    additional_vins: request.vins as NonEmptyString[],
});

export const mapToLoadPreviewErrorCode = (apiErrorCode: ApiLoadPreviewErrorCode): LoadPreviewErrorCode => {
    switch (apiErrorCode) {
        case ApiLoadPreviewErrorCode.UNKNOWN_VIN:
            return LoadPreviewErrorCode.UNKNOWN_VIN;
        case ApiLoadPreviewErrorCode.INCORRECT_STATE:
            return LoadPreviewErrorCode.INCORRECT_STATE;
        case ApiLoadPreviewErrorCode.OTHER_SUPPLIER:
            return LoadPreviewErrorCode.OTHER_SUPPLIER;
        case ApiLoadPreviewErrorCode.CANCELLED:
            return LoadPreviewErrorCode.CANCELLED;
        case ApiLoadPreviewErrorCode.HAS_OUTGOING:
            return LoadPreviewErrorCode.HAS_OUTGOING;
        case ApiLoadPreviewErrorCode.DIFFERENT_MEANS_TYPE:
            return LoadPreviewErrorCode.DIFFERENT_MEANS_TYPE;
        case ApiLoadPreviewErrorCode.VEHICLE_IN_INCOMING:
            return LoadPreviewErrorCode.VEHICLE_IN_INCOMING;
        case ApiLoadPreviewErrorCode.INVALID_LOCATION:
            return LoadPreviewErrorCode.INVALID_LOCATION;
        case ApiLoadPreviewErrorCode.MISSING_PERMISSION:
            return LoadPreviewErrorCode.MISSING_PERMISSION;
    }
};

export const mapToLoadPreviewError = (apiStatusCompletionLoadError: ApiLoadPreviewError): LoadPreviewError => ({
    errorCode: mapToLoadPreviewErrorCode(apiStatusCompletionLoadError.error_code),
    vins: apiStatusCompletionLoadError.vehicles,
});

export const mapToVehicleInUnannouncedLoadPreview = (
    apiVehicle: ApiVehicleInUnannouncedLoadPreview,
): VehicleInUnannouncedLoadPreview => ({
    id: apiVehicle.id,
    vehicle: {
        vin: apiVehicle.vin,
        model: {
            manufacturer: apiVehicle.manufacturer,
            name: apiVehicle.model_name,
            version: apiVehicle.model_version,
        },
        vehicleType: apiVehicle.vehicle_type !== undefined ? mapToVehicleType(apiVehicle.vehicle_type) : undefined,
        finalDestination:
            apiVehicle.final_destination !== undefined ? mapToLocation(apiVehicle.final_destination) : undefined,
    },
});

export const mapToVehicleInLoad = (apiVehicleInLoadPreview: ApiVehicleInLoadPreview): VehicleInLoad => ({
    vin: apiVehicleInLoadPreview.vin,
    model: {
        manufacturer: apiVehicleInLoadPreview.manufacturer,
        name: apiVehicleInLoadPreview.model_name,
        version: apiVehicleInLoadPreview.model_version,
    },
    vehicleType:
        apiVehicleInLoadPreview.vehicle_type !== undefined
            ? mapToVehicleType(apiVehicleInLoadPreview.vehicle_type)
            : undefined,
    finalDestination:
        apiVehicleInLoadPreview.final_destination !== undefined
            ? mapToLocation(apiVehicleInLoadPreview.final_destination)
            : undefined,
});

export const mapToIncomingVehicleInLoadPreview = (
    apiVehicleInLoadPreview: ApiVehicleInLoadPreview,
): IncomingVehicleInLoadPreview => ({
    id: apiVehicleInLoadPreview.id,
    vehicle: mapToVehicleInLoad(apiVehicleInLoadPreview),
    arrival: {
        transportInformation: {
            meansOfTransportType: mapToMeansOfTransportType(apiVehicleInLoadPreview.means_of_transport_type),
            meansOfTransportId: apiVehicleInLoadPreview.means_of_transport_id,
            trailerId: apiVehicleInLoadPreview.trailer_id,
        },
    },
});

export const mapToOutgoingVehicleInLoadPreview = (
    apiVehicleInLoadPreview: ApiVehicleInLoadPreview,
): OutgoingVehicleInLoadPreview => ({
    id: apiVehicleInLoadPreview.id,
    vehicle: mapToVehicleInLoad(apiVehicleInLoadPreview),
    departure: {
        transportInformation: {
            meansOfTransportType: mapToMeansOfTransportType(apiVehicleInLoadPreview.means_of_transport_type),
            meansOfTransportId: apiVehicleInLoadPreview.means_of_transport_id,
            trailerId: apiVehicleInLoadPreview.trailer_id,
        },
    },
});

export const mapToLoadPreviewArrivals = (apiLoadPreviewDepartures: ApiLoadPreview): LoadPreviewArrivals => ({
    vehicles: apiLoadPreviewDepartures.vehicles.map(mapToIncomingVehicleInLoadPreview),
    invalidVins: apiLoadPreviewDepartures.invalid_vehicles.map(mapToLoadPreviewError),
    timeZone: apiLoadPreviewDepartures.compound_time_zone,
});

export const mapToLoadPreviewUnannouncedDepartures = (
    apiLoadPreviewUnannouncedDepartures: ApiUnannouncedLoadPreviewDepartures,
): LoadPreviewUnannouncedDepartures => ({
    vehicles: apiLoadPreviewUnannouncedDepartures.vehicles.map(mapToVehicleInUnannouncedLoadPreview),
    invalidVins: apiLoadPreviewUnannouncedDepartures.invalid_vehicles.map(mapToLoadPreviewError),
    timeZone: apiLoadPreviewUnannouncedDepartures.compound_time_zone,
});

export const mapToLoadPreviewDepartures = (apiLoadPreviewDepartures: ApiLoadPreview): LoadPreviewDepartures => ({
    vehicles: apiLoadPreviewDepartures.vehicles.map(mapToOutgoingVehicleInLoadPreview),
    invalidVins: apiLoadPreviewDepartures.invalid_vehicles.map(mapToLoadPreviewError),
    timeZone: apiLoadPreviewDepartures.compound_time_zone,
});
